import adAccountCRUDActions from '@mm/state/actions/AdAccountCRUDActions';
import MessengerAdCRUDActions from '@mm/state/actions/MessengerAdCRUDActions';
import {
  selectMessengerAd,
  unselectMessengerAd,
} from '@mm/state/actions/viewActions';
import AdAccountModel, {
  emptyAccountPlaceholder,
} from '@mm/state/model/AdAccountModel';
import { MessengerAdModel } from '@mm/state/model/MessengerAdModel';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { LifeCycleThis } from '@mm/utils';
import { uniqBy } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import { Dispatch } from 'redux';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import SelectorWithArrow from '../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import { addNamesToAccounts } from '../../../../../common/helpers/utils';
import AdDetailsSync from './AdDetailsSync/AdDetailsSync';
import ContentBuilder from './ContentBuilder/ContentBuilder';
import InitialSetupInstructions from './InitialSetupInstructions';
import './MessengerAdDetail.scss';
import NameTextInput from './NameTextInput';
import SetupDripCampaign from './SetupDripCampaign';

type OuterProps = {
  usedInSlideout?: boolean;
};
type PropsFromState = ReturnType<typeof mapStateToProps>;
type PropsFromDispatch = ReturnType<typeof mapDispatchToProps>;
type PropsFromHandlers = {
  fbPageAccounts: AdAccountModel[];
  selectedAccount: AdAccountModel;
  setFbPageAccounts: (accounts: AdAccountModel[]) => void;
  setSelectedAccount: (account: AdAccountModel) => void;
  assigningInitialAccount: boolean;
  setAssigningInitialAccount: (value: boolean) => void;
  handleChange: (id: string) => void;
  onScrollEnd: () => void;
};

type InnerProps = RouterComponentProps &
  PropsFromState &
  PropsFromDispatch &
  PropsFromHandlers;
type Props = InnerProps & OuterProps;

const MessengerAdDetail = (props: Props) => {
  const {
    ad,
    activeChatbotId,
    adAccounts,
    fbPageAccounts,
    selectedAccount,
    onScrollEnd,
    handleChange,
    usedInSlideout,
  } = props;

  const accountsWithNames = uniqBy(
    addNamesToAccounts([...(fbPageAccounts || []), ...(adAccounts || [])]),
    'id',
  );
  const selectedAccountWithName = accountsWithNames.find(
    a => a.id === selectedAccount.id,
  );

  return ad ? (
    <>
      <NameTextInput ad={ad} chatbotId={activeChatbotId} />
      <CollapsibleContentContainer
        orderNumber="1"
        containerName="Select Ad Account for the Messenger Ads"
      >
        <SelectorWithArrow
          className="ad-details__ad-account-selector"
          onChange={handleChange}
          selectedItem={selectedAccountWithName as AdAccountModel}
          items={accountsWithNames}
          onScrollEndReached={onScrollEnd}
        />
      </CollapsibleContentContainer>
      <InitialSetupInstructions />
      <ContentBuilder ad={ad} activeChatbotId={activeChatbotId} />
      <AdDetailsSync
        ad={ad}
        accountUserId={selectedAccount.userId}
        adAccountName={selectedAccount.name}
        adAccountId={selectedAccount.id}
        botId={activeChatbotId}
        usedInSlideout={usedInSlideout}
      />
      <SetupDripCampaign />
    </>
  ) : null;
};

async function componentDidMount(this: LifeCycleThis<Props>) {
  const {
    ad,
    location,
    match,
    getMessengerAds,
    setSelectedMessengerAd,
    facebookPageId,
    adAccounts,
    activeChatbotId,
    updateMessengerAd,
    getRemoteAccounts,
    getFbPageAccounts,
    setFbPageAccounts,
    selectedAccount,
    setSelectedAccount,
  } = this.props;

  if (adAccounts.length === 0) {
    getRemoteAccounts(facebookPageId, '');
  }

  const pageAccounts = await getFbPageAccounts(facebookPageId);
  setFbPageAccounts(pageAccounts);

  if (selectedAccount === emptyAccountPlaceholder && ad) {
    const foundAccount =
      pageAccounts.find(a => ad.adAccountId && a.id === ad.adAccountId) ||
      pageAccounts.find(a => a.selected) ||
      pageAccounts[0];
    if (foundAccount) {
      setSelectedAccount(foundAccount);
      if (!ad.adAccountId) {
        await updateMessengerAd(activeChatbotId, {
          ...ad,
          adAccountId: foundAccount.id,
        });
      }
    }
  }

  if (!ad) {
    const { pathname } = location;
    const {
      params: { chatbotId },
    } = match;
    const messengerAdId = Number(pathname.split('/').pop());
    const ads = await getMessengerAds(chatbotId);

    if (ads) {
      const adFromUrl = ads.find(a => a.id === messengerAdId);
      if (adFromUrl) setSelectedMessengerAd(adFromUrl);
    }
  }
}

async function componentDidUpdate(
  this: LifeCycleThis<Props>,
  prevProps: Props,
) {
  const {
    ad,
    activeChatbotId,
    adAccounts,
    getRemoteAccounts,
    getFbPageAccounts,
    setFbPageAccounts,
    facebookPageId,
    selectedAccount,
    setSelectedAccount,
    assignAdAccount,
    assigningInitialAccount,
    setAssigningInitialAccount,
    updateMessengerAd,
    fbPageAccounts,
  } = this.props;

  if (adAccounts.length > 0) {
    const foundAccount =
      (ad && ad.adAccountId && adAccounts.find(a => a.id === ad.adAccountId)) ||
      adAccounts.find(a => a.selected) ||
      adAccounts[0];
    const alreadyAssigned = fbPageAccounts.find(a => a.id === foundAccount.id);
    if (selectedAccount === emptyAccountPlaceholder) {
      if (ad) {
        setSelectedAccount(foundAccount);
        if (ad.adAccountId !== foundAccount.id) {
          await updateMessengerAd(activeChatbotId, {
            ...ad,
            adAccountId: foundAccount.id,
          });
        }
      }
      if (!assigningInitialAccount && !alreadyAssigned) {
        setAssigningInitialAccount(true);
        await assignAdAccount(facebookPageId, foundAccount);
        setFbPageAccounts(await getFbPageAccounts(facebookPageId));
        setAssigningInitialAccount(false);
      }
    }
  }

  if (facebookPageId === prevProps.facebookPageId) {
    return;
  }
  getRemoteAccounts(facebookPageId, '');
  setFbPageAccounts(await getFbPageAccounts(facebookPageId));
}

function componentWillUnmount(this: LifeCycleThis<Props>) {
  this.props.deselectMessengerAd();
}

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  updateMessengerAd: (botId: number, ad: Partial<MessengerAdModel>) =>
    dispatch(MessengerAdCRUDActions.updateLegacy(botId, ad)),
  deselectMessengerAd: () => dispatch(unselectMessengerAd()),
  setSelectedMessengerAd: (ad: MessengerAdModel) =>
    dispatch(selectMessengerAd(ad)),
  getMessengerAds: (botId: number) =>
    dispatch(MessengerAdCRUDActions.retrieveAll(botId)),
  getRemoteAccounts: (fbPageId: number, next: string = '') =>
    dispatch(adAccountCRUDActions.retrieveAll(fbPageId, false, next)),
  getFbPageAccounts: async (fbPageId: number) =>
    dispatch(adAccountCRUDActions.retrieveByFbPageId(fbPageId)),
  assignAdAccount: async (fbPageId: number, adAccount: AdAccountModel) =>
    dispatch(adAccountCRUDActions.assignAdAccount(fbPageId, adAccount, false)),
});

const mapStateToProps = (state: StoreState) => ({
  ad: state.view.chatbotEditor.selectedMessengerAd,
  ads: state.messengerAds.entities,
  adAccounts: state.adAccounts.entities,
  nextCursor: state.adAccounts.next,
  activeChatbotId: activeChatbotIdSelector(state),
  facebookPageId: activeFacebookPageIdSelector(state),
});

const initialState = {
  assigningInitialAccount: false,
  fbPageAccounts: [emptyAccountPlaceholder],
  selectedAccount: emptyAccountPlaceholder,
};

const handleChange =
  ({
    ad,
    assignAdAccount,
    getFbPageAccounts,
    fbPageAccounts,
    facebookPageId,
    setSelectedAccount,
    setFbPageAccounts,
    adAccounts,
    activeChatbotId,
    updateMessengerAd,
  }: Props) =>
  async (id: string) => {
    if (facebookPageId) {
      const adAccount =
        [...fbPageAccounts, ...adAccounts].find(acc => acc.id === id) ||
        emptyAccountPlaceholder;
      if (ad && ad.adAccountId !== adAccount.id) {
        await updateMessengerAd(activeChatbotId, {
          ...ad,
          adAccountId: adAccount.id,
        });
      }
      if (fbPageAccounts.indexOf(adAccount) !== -1) {
        setSelectedAccount(adAccount);
        return;
      }
      await assignAdAccount(facebookPageId, adAccount);
      setSelectedAccount(adAccount);
      setFbPageAccounts(await getFbPageAccounts(facebookPageId));
    }
  };

const onScrollEnd = (props: Props) => () => {
  const { getRemoteAccounts, facebookPageId, nextCursor } = props;

  if (nextCursor) getRemoteAccounts(facebookPageId, nextCursor);
};

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(initialState, {
    setSelectedAccount: () => account => ({ selectedAccount: account }),
    setFbPageAccounts: () => fbPageAccounts => ({ fbPageAccounts }),
    setAssigningInitialAccount: () => assigningInitialAccount => ({
      assigningInitialAccount,
    }),
  }),
  withHandlers({ handleChange, onScrollEnd }),
  lifecycle<Props, {}>({
    componentDidMount,
    componentDidUpdate,
    componentWillUnmount,
  }),
);

export default enhance(MessengerAdDetail);
